import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, ApiError, PasswordLoginResult } from '../types';
import { authApi, userApi } from '../services/api';
import { jwtDecode } from 'jwt-decode';
import {
  IDENTITY_PROVIDERS,
  GOOGLE_STORAGE,
  LINKEDIN_STORAGE,
  clearExternalPendingProfile,
  clearGooglePendingProfile,
  clearGoogleSession,
  clearLinkedInPendingProfile,
  clearLinkedInSession,
  markGoogleSession,
  markLinkedInSession,
  readExternalPendingProfile,
  writeExternalPendingProfile,
} from '../config/identityProviders';
import { clearLegalConsent } from '../utils/legalConsent';
import {
  SSO_TOKEN_EVENT,
  clearLocalToken,
  installSsoListener,
  requestTokenFromOpener,
  writeLocalToken,
} from '../auth/ssoHandshake';
import { getUserProfileId } from '../utils/userProfile';
import { canAccessAdmin } from '../utils/roles';
import { invalidateMyEnrollments } from '../services/enrollmentCache';
import { withTokenRoles } from '../auth/tokenRoles';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isProfileComplete: boolean;
  isOwner: boolean;
  login: (email: string, password: string, from?: string) => Promise<PasswordLoginResult>;
  verifyLoginMfa: (mfaToken: string, code: number, from?: string) => Promise<boolean>;
  resendLoginMfa: (mfaToken: string) => Promise<boolean>;
  register: (email: string, password: string) => Promise<boolean>;
  createProfile: (
    username: string,
    mobile: string,
    linkedInUrl?: string,
    userInfoJson?: string,
    legalConsent?: { termsAccepted: boolean; privacyAccepted: boolean }
  ) => Promise<boolean>;
  logout: (options?: { redirectTo?: string }) => void;
  generateEmailVerificationCode: () => Promise<boolean>;
  verifyEmail: (code: number) => Promise<boolean>;
  fetchUserProfile: () => Promise<boolean>;
  acceptLegalDocuments: (payload: {
    termsAccepted: boolean;
    privacyAccepted: boolean;
    termsVersion?: string;
    privacyVersion?: string;
  }) => Promise<boolean>;
  completeLoginWithToken: (newToken: string, profile?: User | null, from?: string) => Promise<boolean>;
  beginExternalProfileSetup: (setupToken: string, email: string, givenName?: string, mobile?: string, accessToken?: string, identityProviderId?: number, pictureUrl?: string) => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

interface JwtPayload {
  email: string;
  exp: number;
  [key: string]: unknown;
}

function isLoginMfaToken(token: string): boolean {
  try {
    const decoded = jwtDecode<JwtPayload>(token);
    return decoded.idcoder_mfa === 'login';
  } catch {
    return false;
  }
}

type ApiErrorType = Error | { message?: string };

function isDeactivatedError(error: unknown): boolean {
  const message = error instanceof Error
    ? error.message
    : typeof error === 'object' && error && 'message' in error
      ? String((error as { message?: string }).message ?? '')
      : String(error ?? '');
  return message.toLowerCase().includes('deactivated');
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const routeAfterAuth = (navigate: ReturnType<typeof useNavigate>, userProfile: User, from?: string) => {
  if (userProfile.needsLegalAcceptance) {
    navigate('/accept-legal', { replace: true, state: { from } });
    return;
  }
  if (!userProfile.isEmailVerified) {
    navigate('/verify-email', { replace: true, state: { from } });
    return;
  }
  if (userProfile.needsProfilePhoto) {
    navigate('/profile', { state: { requirePhoto: true } });
    return;
  }
  if (canAccessAdmin(userProfile)) {
    navigate('/admin/dashboard');
  } else if (from) {
    navigate(from);
  } else if (userProfile.isEmailVerified) {
    navigate('/dashboard');
  } else {
    navigate('/verify-email');
  }
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const isAuthenticated = !!token && !!user;
  const isProfileComplete = Boolean(
    getUserProfileId(user) && user?.username?.trim() && user?.mobile?.trim()
  );
  const isOwner = user?.role?.toLowerCase() === 'owner';

  useEffect(() => {
    installSsoListener();

    const applySharedToken = async () => {
      const shared = localStorage.getItem('token');
      if (!shared) {
        setToken(null);
        setUser(null);
        return;
      }
      try {
        const decoded = jwtDecode<JwtPayload>(shared);
        if (decoded.exp < Date.now() / 1000) {
          clearLocalToken();
          setToken(null);
          setUser(null);
          return;
        }
        setToken(shared);
        await fetchUserProfile();
      } catch {
        clearLocalToken();
        setToken(null);
        setUser(null);
      }
    };

    const validateAndLoadUser = async () => {
      let storedToken = localStorage.getItem('token');
      if (!storedToken) {
        storedToken = await requestTokenFromOpener();
        if (storedToken) {
          localStorage.setItem('token', storedToken);
        }
      }
      if (!storedToken) {
        try {
          const session = await authApi.session();
          storedToken = session.data.token || null;
          if (storedToken) {
            localStorage.setItem('token', storedToken);
          }
        } catch {
          storedToken = null;
        }
      }
      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      try {
        const decoded = jwtDecode<JwtPayload>(storedToken);
        const currentTime = Date.now() / 1000;

        if (decoded.exp < currentTime) {
          clearLocalToken();
          await authApi.logout().catch(() => undefined);
          setToken(null);
          setUser(null);
          setIsLoading(false);
          return;
        }

        setToken(storedToken);
        await fetchUserProfile();
      } catch (error) {
        console.error('Token validation error:', error);
        clearLocalToken();
        setToken(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    const onSharedToken = () => {
      void applySharedToken();
    };
    window.addEventListener(SSO_TOKEN_EVENT, onSharedToken);
    void validateAndLoadUser();
    return () => window.removeEventListener(SSO_TOKEN_EVENT, onSharedToken);
  }, []);

  const completeLoginWithToken = async (newToken: string, profile?: User | null, from?: string): Promise<boolean> => {
    if (!newToken || isLoginMfaToken(newToken)) {
      return false;
    }
    writeLocalToken(newToken);
    setToken(newToken);

    if (profile?.email) {
      if (profile.isActive === false) {
        clearLocalToken();
        clearLinkedInSession();
        clearGoogleSession();
        setToken(null);
        setUser(null);
        throw new Error('This account has been deactivated. Contact an administrator.');
      }
      const signedIn = withTokenRoles(profile, newToken);
      setUser(signedIn);
      if (signedIn.identityProviderId === IDENTITY_PROVIDERS.LinkedIn) {
        markLinkedInSession();
        clearGoogleSession();
      } else if (signedIn.identityProviderId === IDENTITY_PROVIDERS.Google) {
        markGoogleSession();
        clearLinkedInSession();
      } else {
        clearLinkedInSession();
        clearGoogleSession();
      }
      routeAfterAuth(navigate, signedIn, from);
      return true;
    }

    const decoded = jwtDecode<JwtPayload>(newToken);
    setUser({ email: decoded.email });

    try {
      const profileResponse = await userApi.getProfile();

      if (profileResponse.data.success && profileResponse.data.idUserProfile) {
        const userProfile = withTokenRoles(profileResponse.data.idUserProfile, newToken);
        if (userProfile.isActive === false) {
          clearLocalToken();
          clearLinkedInSession();
          clearGoogleSession();
          setToken(null);
          setUser(null);
          throw new Error('This account has been deactivated. Contact an administrator.');
        }
        setUser(userProfile);
        if (userProfile.identityProviderId === IDENTITY_PROVIDERS.LinkedIn) {
          markLinkedInSession();
          clearGoogleSession();
        } else if (userProfile.identityProviderId === IDENTITY_PROVIDERS.Google) {
          markGoogleSession();
          clearLinkedInSession();
        }
        routeAfterAuth(navigate, userProfile, from);
        return true;
      }

      navigate('/profile-setup');
      return true;
    } catch (error: any) {
      if (isDeactivatedError(error)) {
        clearLocalToken();
        clearLinkedInSession();
        clearGoogleSession();
        setToken(null);
        setUser(null);
        throw error;
      }
      if (error.response?.status === 404 ||
          error.message?.includes('User profile not found') ||
          error.response?.data?.errors?.some((e: any) => e.message?.includes('User profile not found'))) {
        navigate('/profile-setup');
        return true;
      }
      throw error;
    }
  };

  const beginExternalProfileSetup = (
    setupToken: string,
    email: string,
    givenName?: string,
    mobile?: string,
    accessToken?: string,
    identityProviderId: number = IDENTITY_PROVIDERS.LinkedIn,
    pictureUrl?: string
  ) => {
    writeExternalPendingProfile({
      email,
      givenName,
      mobile,
      identityProviderId,
      pictureUrl,
    });
    if (identityProviderId === IDENTITY_PROVIDERS.Google) {
      markGoogleSession(accessToken);
      clearLinkedInSession();
    } else {
      markLinkedInSession(accessToken);
      clearGoogleSession();
    }
    writeLocalToken(setupToken);
    setToken(setupToken);
    setUser({ email, username: givenName, mobile, isMobileVerified: Boolean(mobile) });
    navigate('/profile-setup');
  };

  const login = async (email: string, password: string, from?: string): Promise<PasswordLoginResult> => {
    try {
      const response = await authApi.login(email, password);
      const data = response.data;

      if (data.requiresMfa && data.mfaToken) {
        return { authenticated: false, requiresMfa: true, mfaToken: data.mfaToken };
      }

      const { token: newToken } = data;
      if (newToken) {
        clearLinkedInSession();
        clearGoogleSession();
        return { authenticated: await completeLoginWithToken(newToken, null, from) };
      }
      return { authenticated: false };
    } catch (error: unknown) {
      throw error;
    }
  };

  const verifyLoginMfa = async (mfaToken: string, code: number, from?: string): Promise<boolean> => {
    const response = await authApi.verifyLoginMfa(mfaToken, code);
    const newToken = response.data.token;
    if (!newToken) {
      return false;
    }
    clearLinkedInSession();
    clearGoogleSession();
    return completeLoginWithToken(newToken, null, from);
  };

  const resendLoginMfa = async (mfaToken: string): Promise<boolean> => {
    const response = await authApi.resendLoginMfa(mfaToken);
    return response.data.success !== false;
  };

  const register = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await authApi.register(email, password);

      if (response.data.success && response.data.token) {
        clearLinkedInSession();
        clearGoogleSession();
        return completeLoginWithToken(response.data.token);
      }
      const errors = response.data.errors as ApiError[] | null;
      if (errors && Array.isArray(errors) && errors.length > 0) {
        throw new Error(errors.map(e => e.message).join('\n'));
      }
      throw new Error(response.data.message || 'Registration failed');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      if (message.includes('User already exists') || message.includes('User profile already exists')) {
        return (await login(email, password)).authenticated;
      }
      throw error;
    }
  };

  const createProfile = async (
    username: string,
    mobile: string,
    linkedInUrl?: string,
    userInfoJson?: string,
    legalConsent?: { termsAccepted: boolean; privacyAccepted: boolean }
  ): Promise<boolean> => {
    try {
      const pending = readExternalPendingProfile();
      const termsAccepted = !!legalConsent?.termsAccepted;
      const privacyAccepted = !!legalConsent?.privacyAccepted;

      if (pending) {
        const response = await userApi.createExternalProfile(
          pending.email,
          username,
          mobile,
          pending.identityProviderId,
          linkedInUrl,
          userInfoJson,
          pending.pictureUrl,
          { emailConsentAccepted: true, termsAccepted, privacyAccepted }
        );

        if (response.data.success) {
          if (pending.identityProviderId === IDENTITY_PROVIDERS.Google) {
            clearGooglePendingProfile();
          } else {
            clearLinkedInPendingProfile();
          }
          clearLegalConsent();
          if (response.data.token) {
            writeLocalToken(response.data.token);
            setToken(response.data.token);
          }
          const signedIn = response.data.idUserProfile
            ? withTokenRoles(response.data.idUserProfile, response.data.token || localStorage.getItem('token'))
            : { email: pending.email, isEmailVerified: true };
          setUser(signedIn);
          routeAfterAuth(navigate, signedIn);
          return true;
        }
        throw new Error(response.data.message || 'Failed to create profile');
      }

      const response = await userApi.createProfile(username, mobile, linkedInUrl, userInfoJson, {
        emailConsentAccepted: true,
        termsAccepted,
        privacyAccepted,
      });

      if (response.data.success) {
        clearLegalConsent();
        if (response.data.token) {
          writeLocalToken(response.data.token);
          setToken(response.data.token);
        }
        const profile = response.data.idUserProfile
          ? withTokenRoles(response.data.idUserProfile, response.data.token || localStorage.getItem('token'))
          : null;
        setUser(profile);

        if (profile && !profile.isEmailVerified) {
          await generateEmailVerificationCode();
          navigate('/verify-email');
        } else {
          routeAfterAuth(navigate, profile ?? { email: user?.email || '' });
        }

        return true;
      }
      throw new Error(response.data.message || 'Failed to create profile');
    } catch (error: unknown) {
      throw error;
    }
  };

  const fetchUserProfile = async (): Promise<boolean> => {
    try {
      const response = await userApi.getProfile();

      if (response.data.success && response.data.idUserProfile) {
        if (response.data.idUserProfile.isActive === false) {
          clearLocalToken();
          clearLinkedInSession();
          clearGoogleSession();
          setToken(null);
          setUser(null);
          return false;
        }
        setUser(withTokenRoles(response.data.idUserProfile, localStorage.getItem('token')));
        return true;
      }
      const pending = readExternalPendingProfile();
      if (pending) {
        setUser({
          email: pending.email,
          username: pending.givenName,
          mobile: pending.mobile,
          isMobileVerified: Boolean(pending.mobile),
        });
        return false;
      }
      if (response.data.errors?.some(error => error.message === 'User profile not found')) {
        navigate('/profile-setup');
      }
      return false;
    } catch (error: unknown) {
      if (isDeactivatedError(error)) {
        clearLocalToken();
        clearLinkedInSession();
        clearGoogleSession();
        setToken(null);
        setUser(null);
        return false;
      }
      console.error('Error fetching user profile:', error);
      return false;
    }
  };

  const acceptLegalDocuments = async (payload: {
    termsAccepted: boolean;
    privacyAccepted: boolean;
    termsVersion?: string;
    privacyVersion?: string;
  }): Promise<boolean> => {
    const response = await userApi.acceptLegalDocuments(payload);
    if (!response.data.success || !response.data.idUserProfile) {
      throw new Error(response.data.message || 'Could not save legal acceptance.');
    }
    const profile = withTokenRoles(response.data.idUserProfile, localStorage.getItem('token'));
    setUser(profile);
    routeAfterAuth(navigate, profile);
    return true;
  };

  const generateEmailVerificationCode = async (): Promise<boolean> => {
    try {
      if (!user?.email) {
        throw new Error('User email not found');
      }
      const response = await authApi.generateEmailVerificationCode(user.email);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to generate verification code');
      }
      return true;
    } catch (error: unknown) {
      throw error;
    }
  };

  const verifyEmail = async (code: number): Promise<boolean> => {
    try {
      const response = await authApi.verifyEmail(code);

      if (response.data.success) {
        setUser(prev => prev ? { ...prev, isEmailVerified: true } : null);
        return true;
      }

      const errors = response.data.errors as ApiError[] | null;
      if (errors && Array.isArray(errors) && errors.length > 0) {
        throw new Error(errors.map(e => e.message).join('\n'));
      }
      throw new Error(response.data.message || 'Email verification failed');
    } catch (error: unknown) {
      throw error;
    }
  };

  const logout = (options?: { redirectTo?: string }) => {
    const linkedInAccessToken = sessionStorage.getItem(LINKEDIN_STORAGE.accessToken);
    const googleAccessToken = sessionStorage.getItem(GOOGLE_STORAGE.accessToken);

    void (async () => {
      if (linkedInAccessToken) {
        try {
          await userApi.revokeLinkedIn(linkedInAccessToken);
        } catch {
          // Continue local sign-out even if revoke fails.
        }
      }

      if (googleAccessToken) {
        try {
          await userApi.revokeGoogle(googleAccessToken);
        } catch {
          // Continue local sign-out even if revoke fails.
        }
      }

      try {
        await authApi.logout();
      } catch {
        // Continue local sign-out even if the shared cookie cannot be cleared.
      }
      clearLocalToken();
      clearExternalPendingProfile();
      clearLinkedInSession();
      clearGoogleSession();
      invalidateMyEnrollments();
      setToken(null);
      setUser(null);
      navigate(options?.redirectTo ?? '/', { replace: true });
    })();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        isLoading,
        isAuthenticated,
        isProfileComplete,
        isOwner,
        login,
        verifyLoginMfa,
        resendLoginMfa,
        register,
        createProfile,
        logout,
        generateEmailVerificationCode,
        verifyEmail,
        fetchUserProfile,
        acceptLegalDocuments,
        completeLoginWithToken,
        beginExternalProfileSetup,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
